import { supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";
import { PaymentMethod } from "@/types/constants";
import { Payment } from "./types";
import { ReceiptEmailService } from "../receiptEmailService";
import { InvoiceStatusService } from "../invoice/invoiceStatusService";

export interface ManualPaymentData {
  family_id: string;
  organization_id: string;
  amount_cents: number;
  payment_method: Exclude<PaymentMethod, 'stripe'>;
  paid_at: string;
  check_number?: string;
  manual_notes?: string;
  invoice_id?: string;
}

export const ManualPaymentService = {
  /**
   * Generate a unique receipt number for manual payments
   * Format: RCP-YYYYMMDD-###
   */
  generateReceiptNumber: async (): Promise<string> => {
    const context: ErrorContext = {
      operation: "generateReceiptNumber",
      additionalInfo: {},
    };

    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
      
      // Get the count of receipts created today to generate sequential number
      const { data: todayReceipts, error } = await supabase
        .from('payments')
        .select('receipt_number')
        .like('receipt_number', `RCP-${dateStr}-%`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sequentialNumber = (todayReceipts?.length || 0) + 1;
      const paddedNumber = sequentialNumber.toString().padStart(3, '0');
      
      return `RCP-${dateStr}-${paddedNumber}`;
    } catch (error) {
      handleError(error, "Failed to generate receipt number", ErrorSeverity.ERROR, context);
      // Fallback to timestamp-based receipt number
      const timestamp = Date.now().toString().slice(-6);
      return `RCP-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${timestamp}`;
    }
  },

  /**
   * Validate payment amount against invoice (if linked)
   */
  validatePaymentAmount: InvoiceStatusService.validatePaymentAmount,

  /**
   * Create a new manual payment record
   */
  createManualPayment: async (data: ManualPaymentData): Promise<Payment | null> => {
    const context: ErrorContext = {
      operation: "createManualPayment",
      additionalInfo: { 
        family_id: data.family_id, 
        organization_id: data.organization_id,
        payment_method: data.payment_method 
      },
    };

    try {
      // Validate payment amount if linked to an invoice
      if (data.invoice_id) {
        const validation = await ManualPaymentService.validatePaymentAmount(data.invoice_id, data.amount_cents);
        if (!validation.valid) {
          throw new Error(validation.message);
        }
      }
      const receiptNumber = await ManualPaymentService.generateReceiptNumber();
      
      // Ensure paid_at is always stored as proper UTC timestamp with Z suffix
      const paidAtUtc = new Date(data.paid_at).toISOString();

      const paymentData = {
        family_id: data.family_id,
        organization_id: data.organization_id,
        amount_cents: data.amount_cents,
        payment_method: data.payment_method,
        paid_at: paidAtUtc,
        check_number: data.check_number,
        manual_notes: data.manual_notes,
        invoice_id: data.invoice_id,
        receipt_number: receiptNumber,
        status: 'succeeded', // Manual payments are immediately succeeded
        stripe_payment_intent: null, // No Stripe involvement
      };

      const { data: insertedPayment, error } = await supabase
        .from('payments')
        .insert(paymentData)
        .select()
        .single();

      if (error) throw error;

      // If payment is linked to an invoice, calculate and update the invoice status
      if (data.invoice_id) {
        const newStatus = await InvoiceStatusService.calculateInvoiceStatus(data.invoice_id);
        await InvoiceStatusService.updateInvoiceStatus(data.invoice_id, newStatus);
      }

      return insertedPayment as Payment;
    } catch (error) {
      handleError(error, "Failed to create manual payment", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Calculate the correct invoice status based on payments received
   */
  calculateInvoiceStatus: InvoiceStatusService.calculateInvoiceStatus,

  /**
   * Update invoice status when a payment is applied
   */
  updateInvoiceStatus: InvoiceStatusService.updateInvoiceStatus,

  /**
   * Get unpaid invoices for a family (includes unpaid, overdue, and partial invoices for manual payment dialog)
   */
  getUnpaidInvoicesForFamily: async (familyId: string, organizationId: string) => {
    const context: ErrorContext = {
      operation: "getUnpaidInvoicesForFamily",
      additionalInfo: { familyId, organizationId },
    };

    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, total_cents, due_date, created_at')
        .eq('family_id', familyId)
        .eq('organization_id', organizationId)
        .in('status', ['unpaid', 'overdue', 'partial'])
        .order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleError(error, "Failed to fetch outstanding invoices", ErrorSeverity.WARNING, context);
      return [];
    }
  },

  /**
   * Send receipt email for a manual payment
   */
  sendPaymentReceiptEmail: async (payment: Payment): Promise<boolean> => {
    const context: ErrorContext = {
      operation: "sendPaymentReceiptEmail",
      additionalInfo: { paymentId: payment.id },
    };

    try {
      // Fetch organization data
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('name, contact_email, contact_phone, logo_url')
        .eq('id', payment.organization_id)
        .single();

      if (orgError) throw orgError;

      // Fetch family data with primary parent email
      const { data: familyData, error: familyError } = await supabase
        .from('families')
        .select(`
          name,
          parents!inner(email, is_primary)
        `)
        .eq('id', payment.family_id)
        .single();

      if (familyError) throw familyError;

      // Find primary parent email
      const primaryParent = familyData.parents?.find((parent: any) => parent.is_primary);
      const family = {
        name: familyData.name,
        primary_parent_email: primaryParent?.email,
      };

      // Fetch invoice data if payment is linked to an invoice
      let invoice;
      if (payment.invoice_id) {
        const { data: invoiceData, error: invoiceError } = await supabase
          .from('invoices')
          .select('id, total_cents, due_date')
          .eq('id', payment.invoice_id)
          .single();

        if (!invoiceError && invoiceData) {
          invoice = invoiceData;
        }
      }

      // Send receipt email
      return await ReceiptEmailService.sendPaymentReceipt({
        payment,
        organization,
        family,
        invoice,
      });
    } catch (error) {
      handleError(error, "Failed to send payment receipt email", ErrorSeverity.WARNING, context);
      return false;
    }
  },
};